import { useMemo } from "react";
import { useOnboardingStore } from "@/stores/onboarding-store";
import {
  useConversationStore,
  type ConversationMessage,
} from "@/stores/conversation-store";
import { useCanvasStore, type AwaitingInput } from "@/stores/canvas-store";
import {
  useCompanionStore,
  type ActiveExecution,
} from "@/stores/companion-store";
import { contextualConversationExecution } from "./contextual-conversation-execution";
import {
  contextualDiscoveryPresentationHeld,
  contextualRootExecutionIds,
} from "./contextual-discovery-presentation";
import type { ContextualStoryState } from "./contextual-story-state";

type OnboardingState = ReturnType<typeof useOnboardingStore.getState>;

/** The contextual story while its desktop step is showing. */
export function activeContextualStory(
  state: Pick<OnboardingState, "restarting" | "step" | "contextualStory">,
): ContextualStoryState | null {
  return !state.restarting && state.step === "desktop_usage_primer"
    ? state.contextualStory
    : null;
}

/** Resolve the accepted execution for this first-work conversation. */
export function useContextualCursorExecution(): string | null {
  const story = useOnboardingStore(activeContextualStory);
  const messages = useConversationStore((state) => state.messages);
  const executions = useCompanionStore((state) => state.executions);
  return contextualConversationExecution(story, messages, executions.values());
}

/** Only the guide and farewell replace the normal production cursor. */
export function contextualCursorExecutionIds(
  story: ContextualStoryState | null,
  messages: ConversationMessage[],
  executions: Map<string, ActiveExecution>,
  awaitingInputs: Map<string, AwaitingInput[]>,
): string[] {
  if (story && contextualDiscoveryPresentationHeld(story))
    return contextualRootExecutionIds(story, messages, executions.values());
  if (!story || ["looking", "live"].includes(story.phase)) return [];
  const executionId = contextualConversationExecution(
    story,
    messages,
    executions.values(),
  );
  return [
    ...new Set([...(story.executionIds ?? []), story.executionId, executionId]),
  ].filter(
    (id): id is string =>
      !!id &&
      ![...awaitingInputs.values()]
        .flat()
        .some((item) => item.executionId === id),
  );
}

export function useContextualCursorExecutions(): string[] {
  const story = useOnboardingStore(activeContextualStory);
  const awaitingInputs = useCanvasStore((state) => state.awaitingInputs);
  const messages = useConversationStore((state) => state.messages);
  const executions = useCompanionStore((state) => state.executions);
  return useMemo(
    () =>
      contextualCursorExecutionIds(story, messages, executions, awaitingInputs),
    [story, awaitingInputs, messages, executions],
  );
}
